import { secondsToDuration } from '@client/new/helpers/time';
import { Slider } from '@mui/material';

export type VideoTrimFrameProps = {
  value: [number, number];
  onChange: (range: [number, number]) => void;
  duration: number;
  limitTimeRangeDistance: number;
  isMaxRangeLimit: boolean;
};

export type GetFrameRangeValuesProps = {
  isMaxRangeLimit: boolean;
  newValue: [number, number];
  limitTimeRangeDistance: number;
  activeThumb: number;
};

export const getFrameRangeValues = (
  p: GetFrameRangeValuesProps,
): [number, number] => {
  const [start, end] = p.newValue;
  if (p.isMaxRangeLimit) {
    const isMaxRangeExceeded = end - start > p.limitTimeRangeDistance;
    if (!isMaxRangeExceeded) return p.newValue;

    return p.activeThumb === 0
      ? [start, start + p.limitTimeRangeDistance]
      : [end - p.limitTimeRangeDistance, end];
  }

  const isBelowMinRange = end - start <= p.limitTimeRangeDistance;
  if (!isBelowMinRange) return [start, end];

  if (p.activeThumb === 0) {
    const clamped = Math.min(start, 100 - p.limitTimeRangeDistance);
    return [clamped, clamped + p.limitTimeRangeDistance];
  }
  const clamped = Math.max(end, p.limitTimeRangeDistance);
  return [clamped - p.limitTimeRangeDistance, clamped];
};

export const VideoTrimFrame = (p: VideoTrimFrameProps) => {
  const onChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }
    const calculatedValues = getFrameRangeValues({
      isMaxRangeLimit: p.isMaxRangeLimit,
      newValue: [newValue[0], newValue[1]],
      limitTimeRangeDistance: p.limitTimeRangeDistance,
      activeThumb,
    });
    p.onChange(calculatedValues);
  };

  const onChangeCommitted = (
    event: Event | React.SyntheticEvent<Element, Event>,
    value: number | number[],
  ) => {
    p.onChange(value as [number, number]);
  };

  function valueLabelFormat(value: number) {
    if (p.duration === 0) return 0;
    const durationByPercent = (value / 100) * p.duration;
    return secondsToDuration(Number(durationByPercent));
  }

  return (
    <Slider
      size="small"
      defaultValue={[0, 100]}
      min={0}
      step={0.01}
      max={100}
      value={p.value}
      valueLabelFormat={valueLabelFormat}
      valueLabelDisplay="auto"
      onChange={onChange}
      onChangeCommitted={onChangeCommitted}
      disableSwap={true}
      sx={{
        height: `calc(100% + 10px)`,
        width: `calc(100% + 4px)`,
        borderRadius: 0,
        position: 'absolute',
        color: 'black',
        margin: 0,
        padding: 0,
        top: '-6px',
        left: '-2px',
        pointerEvents: 'none',
        '& .MuiSlider-thumb': {
          pointerEvents: 'all !important',
        },
        '& .MuiSlider-thumb:nth-of-type(3)': {
          borderRadius: ' 10px 0px 0px 10px',
          height: '100%',
          top: '50%',
          left: 0,
          width: '5px',
          background: '#ffcc02',
          textAlign: 'justify',
          '&::before': {
            content: '"||"',
            fontSize: '20px',
            width: '15px',
            height: '100%',
            background: '#ffcc02',
            left: '-10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
        '& .MuiSlider-thumb:hover': {
          boxShadow: 'none',
        },
        '& .Mui-active': {
          boxShadow: 'none',
        },
        '& .MuiSlider-thumb:nth-of-type(4)': {
          borderRadius: ' 0px 10px 10px 0px',
          height: '100%',
          top: '50%',
          right: 0,
          width: '5px',
          background: '#ffcc02',
          '&::before': {
            content: '"||"',
            fontSize: '20px',
            width: '15px',
            height: '100%',
            background: '#ffcc02',
            textAlign: 'center',
            right: '-10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
        '.MuiSlider-track': {
          height: '100%',
          top: '50%',
          background: 'transparent',
          borderTop: '6px solid #ffcc02',
          borderBottom: '6px solid #ffcc02',
        },
        '.MuiSlider-rail': {
          height: '100%',
          top: '50%',
          color: 'transparent',
          background: 'transparent',
        },
      }}
    />
  );
};
